"use client";

import { usePublicProfile } from "@knowt/syncing/hooks/user/useUserData";
import { UserDetails } from "@knowt/syncing/graphql/schema";
import { stringWithLimit } from "@knowt/syncing/utils/stringUtils";
import clsx from "clsx";
import Image from "next/image";
import styles from "../../profilepage.module.css";
import { PROFILE_HEADER_ID } from "../../../utils/colors";
import ProfileHeaderInitialColorHandler from "./components/ProfileHeaderInitialColorHandler";
import UserStats from "./components/UserStats";
import Link from "@/components/wrappers/Link";
import UserAvatar from "@/components/userCardComponents/UserAvatar";
import UserNameWithBadge from "@/components/UserNameWithBadge";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { ConditionalLink } from "@/components/ConditionalLink";
import { useSchoolName } from "@knowt/syncing/hooks/schools/useSchool";
import br from "@/styles/breakpoints.module.css";
import { Calendar } from "lucide-react";
import { convertUnixToDateTime } from "@knowt/syncing/utils/dateTimeUtils";
import Mountable from "@/components/styled/div/Mountable";
import { themeColors } from "@/utils/themeColors";
import FilledTextButton from "@/components/CircularButton/styled/FilledTextButton";
import { isDarkColor } from "@knowt/syncing/utils/genericUtils";
import { ShareIcon } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";
import { usePathname } from "next/navigation";
import CircularButton from "@/components/CircularButton";
import { getProfileDynamicTextColor } from "../../utils";

export type ProfileHeaderProps = {
    user: UserDetails;
    totalNotes?: number;
    totalFlashcardSets?: number;
    totalBlogs?: number;
    isEditable: boolean;
};

const ShareProfileBtn = ({ userProfile }: { userProfile: UserDetails }) => {
    const pathname = usePathname();

    return (
        <CircularButton
            onClick={async () => {
                copyToClipboard(`Check out my profile on Knowt!\nknowt.com${pathname}`);
            }}
            radius="4rem"
            sx={{
                padding: "0.8rem",
                backgroundColor: `${userProfile.profileColor || themeColors.neutralWhite}`,
                border: `1px solid ${getProfileDynamicTextColor({ userProfile })}`,
            }}>
            <ShareIcon size={18} color={getProfileDynamicTextColor({ userProfile })} />
        </CircularButton>
    );
};

const ProfileHeader = ({ user: _userProfile, isEditable }: ProfileHeaderProps) => {
    const { userData: userProfile } = usePublicProfile(_userProfile.ID, _userProfile);
    const schoolName = useSchoolName(userProfile.schoolId || "");

    return (
        <FlexColumn
            id={PROFILE_HEADER_ID}
            className={clsx(styles.profileHeader)}
            style={{
                overflow: "hidden",
                backgroundColor: `${userProfile.profileColor || themeColors.neutralWhite}`,
                color: "var(--color-profile-dynamic-text-color)",
                borderRadius: "2rem",
                marginBottom: "2rem",
            }}>
            <ProfileHeaderInitialColorHandler user={userProfile} />
            {/* HEADER COVER */}
            <div
                className={clsx(styles.coverImage, styles.smDownBorderRadius0, styles.smDownMargin0, br.smDownWidth100)}
                style={{
                    alignSelf: "center",
                    position: "relative",
                    borderRadius: "2rem",
                    overflow: "hidden",
                    width: "calc(100% - 1.25rem * 2)",
                    margin: "0.8rem 1.25rem 0rem",
                }}
                data-testid="auto-id-c52a62e4-9a42-49c2-8727-c4994860a0d1">
                {userProfile.cover ? (
                    <Image
                        src={userProfile.cover}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        alt="cover image"
                        width={1000}
                        height={200}
                        quality={100}
                    />
                ) : (
                    <div
                        style={{ backgroundColor: "#ebf2df", height: "100%", width: "100" }}
                        data-testid="auto-id-5cf46546-5003-4e7b-9b12-81a262ae3a11" />
                )}
                <FlexRowAlignCenter style={{ gap: "1rem", position: "absolute", top: "1rem", right: "1rem" }}>
                    {isEditable && (
                        <Link
                            href="/settings"
                            data-testid="auto-id-9491f831-b6a3-425a-924a-0c29d0a25e76">
                            <FilledTextButton className="secondaryTextBold1">Edit profile</FilledTextButton>
                        </Link>
                    )}
                </FlexRowAlignCenter>
            </div>
            {/* PROFILE ITEMS BELOW COVER */}
            <FlexColumn className={styles.smDownMargin0} style={{ padding: "0 1.25rem 2rem", marginLeft: "1.05rem" }}>
                <FlexRowAlignCenter style={{ justifyContent: "space-between", margin: "2rem 0 0" }}>
                    <ConditionalLink condition={isEditable} href={"/settings"}>
                        <UserAvatar
                            radius={"12rem"}
                            className={styles.userAvatarSizeAndPosAdj}
                            src={userProfile.pictureUrl || ""}
                            alt={userProfile.Name || "no profile picture"}
                            sx={{
                                cursor: isEditable ? "pointer" : "normal",
                                border: `0.5rem solid var(--color-profile-dynamic-bg-color)`,
                            }}
                            slotProps={{
                                img: { style: { scale: userProfile.pictureUrl ? "1" : "1.2" } },
                            }}
                        />
                    </ConditionalLink>
                    <ShareProfileBtn userProfile={userProfile} />
                </FlexRowAlignCenter>

                <FlexColumn style={{ marginBottom: "2rem" }}>
                    <UserNameWithBadge
                        user={userProfile}
                        type="Name"
                        className={clsx("ellipsisText", "bodyBold2")}
                        style={{ marginBottom: "0.5rem" }}
                    />
                    <FlexRowAlignCenter style={{ gap: "1rem", marginBottom: "0.5rem" }}>
                        <span
                            className="secondaryText1"
                            style={{
                                maxWidth: "55rem",
                                color: userProfile.profileColor
                                    ? isDarkColor(userProfile.profileColor)
                                        ? themeColors.pureWhite
                                        : themeColors.pureBlack
                                    : themeColors.neutralBlack,
                            }}
                            data-testid="auto-id-6b3676b6-03c3-4a8c-bfc9-8722da638d9f">
                            @{userProfile?.username} <span
                            className={br.smDownDisplayNone}
                            data-testid="auto-id-0b6a11a1-9ad1-47dc-b189-cf8a5b9005ec">{schoolName}</span>
                        </span>
                        {userProfile.created && (
                            <FlexRowAlignCenter style={{ gap: "0.5rem" }} className="secondaryText1">
                                <Calendar size={18} />
                                <p data-testid="auto-id-979075d4-268c-4327-9210-85a47f5bea6a">Joined {convertUnixToDateTime(userProfile?.created).date}</p>
                            </FlexRowAlignCenter>
                        )}
                    </FlexRowAlignCenter>
                    <Mountable mount={false}>
                        <span
                            className={clsx(br.smDownDisplayNone, "secondaryText1")}
                            style={{
                                maxWidth: "55rem",
                                marginTop: "1rem",
                                color: isDarkColor(userProfile.profileColor || themeColors.neutralWhite)
                                    ? themeColors.pureWhite
                                    : themeColors.pureBlack,
                            }}
                            data-testid="auto-id-c9446c7f-008f-4fa6-8241-745b80454015">
                            {stringWithLimit(userProfile.bio as string, 220)}
                        </span>
                    </Mountable>
                </FlexColumn>

                <UserStats user={userProfile} isEditable={isEditable} />
            </FlexColumn>
        </FlexColumn>
    );
};

export default ProfileHeader;
